import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IndexCardsStore } from '../indexcards.store';
import { normalizeName } from '../normalize-name';
import { ConfirmService } from '../../shared/confirm-dialog/confirm-dialog.service';

@Component({
    selector: 'app-medarbejdere-afdelinger-modal-ui',
    templateUrl: './medarbejdere-afdelinger-modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MedarbejdereAfdelingerModal {
    @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;
    private readonly confirmService = inject(ConfirmService);
    readonly indexCardsStore = inject(IndexCardsStore);
    readonly name = signal('');
    readonly busy = signal(false);
    readonly error = signal('');
    readonly normalizedName = computed(() => normalizeName(this.name()));
    readonly unavailable = computed(() => this.busy() || this.indexCardsStore.loading() || this.indexCardsStore.loadError());
    readonly items = computed(() => [...this.indexCardsStore.afdelinger()]
        .sort((a, b) => (a.afdelingsnavn ?? '').localeCompare(b.afdelingsnavn ?? '', 'da'))
        .map(item => ({ ...item, employeeCount: this.employeeCount(item.id) })));
    readonly duplicate = computed(() => this.indexCardsStore.afdelinger().some(item =>
        normalizeName(item.afdelingsnavn).toLocaleLowerCase('da') === this.normalizedName().toLocaleLowerCase('da')));

    constructor(private readonly activeModal: NgbActiveModal) {}

    private employeeCount(id: number): number {
        return this.indexCardsStore.users().filter(user => user.afdeling === id).length;
    }

    onDismiss(): void {
        if (!this.busy()) { this.activeModal.close(); }
    }

    async deleteInstance(item: { id: number; afdelingsnavn: string }): Promise<void> {
        if (this.unavailable()) { return; }
        this.busy.set(true);
        this.error.set('');
        try {
            const count = this.employeeCount(item.id);
            const consequence = count
                ? `Tilknytningen fjernes fra ${count} medarbejdere. Medarbejderne slettes ikke.`
                : 'Denne afdeling har ingen tilknyttede medarbejdere.';
            const confirmed = await this.confirmService.question(
                `Slet “${item.afdelingsnavn}”? ${consequence}`, 'Slet afdeling', 'Annuller');
            if (confirmed && !await this.indexCardsStore.deleteAfdeling(item.id)) {
                this.error.set('Afdelingen kunne ikke slettes. Prøv igen.');
            }
        } finally { this.busy.set(false); }
    }

    async newInstance(): Promise<void> {
        if (this.unavailable() || !this.normalizedName() || this.duplicate()) { return; }
        this.busy.set(true);
        this.error.set('');
        try {
            if (await this.indexCardsStore.createAfdeling(this.normalizedName())) {
                this.name.set('');
            } else {
                this.error.set('Afdelingen kunne ikke oprettes. Navnet er bevaret; prøv igen.');
            }
        } finally {
            this.busy.set(false);
            // Restore focus after Angular removes the disabled state.
            setTimeout(() => this.nameInput?.nativeElement.focus());
        }
    }
}
